import { HTMLAttributes, useMemo } from 'react';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { Button, Dropdown, Menu, Steps, Tabs } from 'antd';
import Icon, {
  LoadingOutlined,
  ReloadOutlined,
  DownOutlined,
  EllipsisOutlined,
} from '@ant-design/icons';
import dayjs from 'lib/dayjs';
import Copy from './Copy';
import { Between } from './TradeRefund';
import { LogisticsPackage, getTranslation } from './useTradeTranslate';
import Description from './Description';
import { SendLogisticsInfo } from '../components/SendLogisticsInfo';
import { ReactComponent as TrackingNumberIcon } from './assets/trackingNumber.svg';
import { ReactComponent as WarehouseTypeIcon } from './assets/warehouseType.svg';
import { css } from '@emotion/css';
import { LogisticsPlatform, useLogisticsListBusiness } from './useLogisticsListBusiness';
import { TrackingClass } from 'components/TrackingClass';
import { MediaViewer, MediaItem } from 'components/MediaViewer';
import { useTradeListContext } from './TradeContext';
import { LogisticsImageSendButton } from './LogisticsImageSendButton';

const Container = styled.div`
  position: relative;
  .ant-tabs {
    margin-top: -15px;
    .ant-tabs-nav {
      padding: 0 14px;
    }
  }
`;

const Toolbar = styled(Between)`
  position: sticky;
  top: 0;
  height: 40px;
  border-bottom: solid 1px var(--fett-border-default);
  background: var(--fett-bg-elevated);
  z-index: 1;
  padding: 0 8px;
  margin-bottom: 0px !important;
`;

const LogisticsDetail = styled.div`
  > section {
    padding: 4px 8px;
    border-bottom: solid 1px var(--fett-border-default);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  > .${({ theme }) => theme.getAntdPrefixedClassName('steps')} {
    padding: 0 10px;
  }

  .ant-steps .ant-steps-item .ant-steps-item-title {
    color: var(--fett-text-secondary);
    font-size: 14px;
    line-height: 24px;
  }
  .ant-steps-item-description {
    padding-bottom: 8px !important;
  }
`;

export interface LogisticsListProps<T extends LogisticsPlatform>
  extends HTMLAttributes<HTMLElement> {
  platform: T;
  logisticsPackageList: LogisticsPackage<T>[];
  fold?: boolean;
  foldNum?: number;
  showCopyAll?: boolean;
  isThird?: boolean;
  isReverse?: boolean;
  externalReturnId?: string;
  exportMode?: boolean;
  sendable?: boolean;
  showLogisticsPackageList?: LogisticsPackage<T>[];
  reportTranslation?: boolean;
  onHide?: () => void;
}

function LogisticsNumber({
  trackingNumber,
  firstMileTrackingNumber,
  lastMileTrackingNumber,
  plpNumber,
  shippingCarrier,
  warehouseCode,
  gxbLogisticsNo,
  logisticsNo,
  trackingClass,
}: {
  trackingNumber: string;
  firstMileTrackingNumber?: string;
  lastMileTrackingNumber?: string;
  plpNumber?: string;
  shippingCarrier?: string;
  warehouseCode?: string;
  gxbLogisticsNo?: string;
  logisticsNo?: string;
  trackingClass?: 'BOOKING';
}) {
  const { t } = useTranslate();

  return (
    <div className="flex flex-col gap-1 text-[var(--fett-text-secondary)]">
      {trackingNumber && (
        <div className="flex items-center gap-1">
          <Icon className="text-[16px]" component={TrackingNumberIcon} />
          <span className="text-[14px] leading-[18px]">{trackingNumber}</span>
          <Copy
            text={trackingNumber}
            successMessage={t('common.feedback.copySuccess')}
            failedMessage={t('common.feedback.copyFailed')}
          />
        </div>
      )}
      {firstMileTrackingNumber && (
        <div className="flex items-center gap-1">
          <Icon className="text-[16px]" component={TrackingNumberIcon} />
          <span className="text-[14px] leading-[18px]">{firstMileTrackingNumber}</span>
          <Copy
            text={firstMileTrackingNumber}
            successMessage={t('common.feedback.copySuccess')}
            failedMessage={t('common.feedback.copyFailed')}
          />
        </div>
      )}
      {lastMileTrackingNumber && (
        <div className="flex items-center gap-1">
          <Icon className="text-[16px]" component={TrackingNumberIcon} />
          <span className="text-[14px] leading-[18px]">{lastMileTrackingNumber}</span>
          <Copy
            text={lastMileTrackingNumber}
            successMessage={t('common.feedback.copySuccess')}
            failedMessage={t('common.feedback.copyFailed')}
          />
        </div>
      )}
      {plpNumber && (
        <div className="flex items-center gap-1">
          <Icon className="text-[16px]" component={TrackingNumberIcon} />
          <span className="text-[14px] leading-[18px]">{plpNumber}</span>
          <Copy
            text={plpNumber}
            successMessage={t('common.feedback.copySuccess')}
            failedMessage={t('common.feedback.copyFailed')}
          />
        </div>
      )}
      {shippingCarrier && (
        <div className="flex items-center gap-1">
          <Icon className="text-[16px]" component={WarehouseTypeIcon} />
          <span className="min-w-0 whitespace-pre-line break-words text-[14px] leading-[18px]">
            {shippingCarrier}
          </span>
        </div>
      )}
      {warehouseCode && (
        <div className="flex items-center gap-1">
          <Icon className="text-[16px]" component={WarehouseTypeIcon} />
          <span className="text-[14px] leading-[18px]">{warehouseCode}</span>
        </div>
      )}
      {gxbLogisticsNo && (
        <div className="flex items-center gap-1">
          <Icon className="text-[16px]" component={TrackingNumberIcon} />
          <span className="text-[14px] leading-[18px]">{gxbLogisticsNo}</span>
          <Copy
            text={gxbLogisticsNo}
            successMessage={t('common.feedback.copySuccess')}
            failedMessage={t('common.feedback.copyFailed')}
          />
        </div>
      )}
      {logisticsNo && (
        <div className="flex items-center gap-1">
          <Icon className="text-[16px]" component={TrackingNumberIcon} />
          <span className="text-[14px] leading-[18px]">{logisticsNo}</span>
          <Copy
            text={logisticsNo}
            successMessage={t('common.feedback.copySuccess')}
            failedMessage={t('common.feedback.copyFailed')}
          />
        </div>
      )}
      <TrackingClass trackingClass={trackingClass} />
    </div>
  );
}

function LogisticsList<T extends LogisticsPlatform>(props: LogisticsListProps<T>) {
  const {
    platform,
    logisticsPackageList,
    fold = false,
    foldNum = 2,
    showCopyAll = true,
    isThird = false,
    isReverse = false,
    externalReturnId,
    exportMode = false,
    sendable = true,
    showLogisticsPackageList = logisticsPackageList,
    reportTranslation,
    onHide,
    ...otherProps
  } = props;

  const { t } = useTranslate();
  const { conversation } = useTradeListContext();

  const {
    needTranslate,
    displayTranslation,
    translating,
    translationError,
    foldCtl,
    isTikTok,
    setFoldCtl,
    onToggleTranslation,
    onRetranslate,
    onCopyAll,
    onSendTiktokLogisticsCard,
  } = useLogisticsListBusiness({
    conversation,
    logisticsPackageList,
    fold,
    exportMode,
    isThird,
    reportTranslation,
    isReverse,
  });

  const transUI = useMemo(() => {
    if (!needTranslate) {
      return null;
    }

    if (translating) {
      return (
        <Button style={{ padding: 0 }} type="link" size="small" loading icon={<LoadingOutlined />}>
          {t('scene.chatWorkspace.orderTab.logistics.action.translating')}
        </Button>
      );
    }

    if (translationError) {
      return (
        <Button
          style={{ padding: 0 }}
          type="link"
          size="small"
          icon={<ReloadOutlined />}
          onClick={onToggleTranslation}
        >
          {t('scene.chatWorkspace.orderTab.logistics.action.translationError')}
        </Button>
      );
    }

    return [
      <Button
        style={{ padding: 0, marginRight: '4px' }}
        type="link"
        size="small"
        onClick={onToggleTranslation}
      >
        {t(
          `scene.chatWorkspace.orderTab.logistics.action.${
            displayTranslation ? 'viewOriginal' : 'viewTranslation'
          }`,
        )}
      </Button>,
      ...(displayTranslation
        ? [
            <Button style={{ padding: 0 }} type="link" size="small" onClick={onRetranslate}>
              {t('scene.chatWorkspace.orderTab.logistics.action.retranslate')}
            </Button>,
          ]
        : []),
    ];
  }, [
    translating,
    translationError,
    t,
    displayTranslation,
    onToggleTranslation,
    needTranslate,
    onRetranslate,
  ]);
  const copyAllUI = (
    <Button style={{ padding: 0 }} type="link" size="small" onClick={onCopyAll}>
      {t('scene.chatWorkspace.orderTab.logistics.action.copyAll')}
    </Button>
  );

  const list = showLogisticsPackageList.map((logisticsPackage) => {
    if (externalReturnId && (logisticsPackage as any).externalReturnId !== externalReturnId) {
      return null;
    }

    const { trackingNumber, trackingInfo, shippingCarrier } = logisticsPackage;

    const {
      externalTradeId,
      packageNumber,
      gxbLogisticsNo,
      logisticsNo,
      firstMileTrackingNumber,
      lastMileTrackingNumber,
      plpNumber,
      trackingClass,
    } = logisticsPackage as LogisticsPackage<'SHOPEE'>;

    const { warehouseCode } = logisticsPackage as LogisticsPackage<'LAZADA'>;

    return (
      <LogisticsDetail key={trackingNumber}>
        <section>
          <LogisticsNumber
            trackingNumber={trackingNumber}
            firstMileTrackingNumber={firstMileTrackingNumber}
            lastMileTrackingNumber={lastMileTrackingNumber}
            plpNumber={plpNumber}
            shippingCarrier={shippingCarrier}
            warehouseCode={warehouseCode}
            gxbLogisticsNo={gxbLogisticsNo}
            logisticsNo={logisticsNo}
            trackingClass={trackingClass}
          />
          {sendable && isTikTok && conversation && packageNumber && (
            <Button
              type="link"
              onClick={() =>
                onSendTiktokLogisticsCard({
                  orderId: externalTradeId,
                  packageId: packageNumber,
                })
              }
            >
              {t('scene.chatWorkspace.orderTab.logistics.action.sendCard')}
            </Button>
          )}
          {!showCopyAll && !exportMode ? (
            <Dropdown
              placement="bottomRight"
              overlay={
                <Menu>
                  <Menu.Item>{copyAllUI}</Menu.Item>
                  {Array.isArray(transUI) ? (
                    transUI.map((item) => <Menu.Item>{item}</Menu.Item>)
                  ) : transUI ? (
                    <Menu.Item>{transUI}</Menu.Item>
                  ) : null}
                </Menu>
              }
            >
              <Button type="text" icon={<EllipsisOutlined />} />
            </Dropdown>
          ) : null}
        </section>

        <Steps progressDot direction="vertical" current={0} size="small" className={stepsClassName}>
          {trackingInfo
            .slice(0, foldCtl ? foldNum : trackingInfo.length)
            .map(
              (
                tracking:
                  | Util.ElementType<Conversation.Trade.Logistics['trackingInfo']>
                  | Util.ElementType<Conversation.Trade.LazadaLogisticsPackage['trackingInfo']>,
              ) => {
                const { updateTime, description, descriptionTranslation, attachment } = tracking;

                const translation = getTranslation(descriptionTranslation);
                const text = displayTranslation ? translation : description;

                const mediaItems: MediaItem[] | undefined =
                  attachment?.length > 0
                    ? attachment.map((item) => ({
                        type: 'image',
                        src: item.url,
                        thumb: item.url,
                      }))
                    : undefined;

                const validUpdateTime = Number(updateTime);
                const timeText = validUpdateTime
                  ? dayjs(validUpdateTime * 1000).format('YYYY-MM-DD HH:mm')
                  : '';

                return (
                  <Steps.Step
                    key={updateTime || description}
                    title={
                      <div className="flex flex-col gap-1">
                        <Description text={text} />
                        {mediaItems && (
                          <MediaViewer
                            items={mediaItems}
                            thumbSize={40}
                            toolbarRender={({ images, index, onClose }) => {
                              const item = images[index];
                              if (item.src && sendable) {
                                return (
                                  <LogisticsImageSendButton imageUrl={item.src} onClose={onClose} />
                                );
                              }
                            }}
                          />
                        )}
                      </div>
                    }
                    description={timeText || undefined}
                  />
                );
              },
            )}
        </Steps>
        {foldCtl && foldNum < trackingInfo.length ? (
          <Button
            type="text"
            size="small"
            style={{
              color: 'var(--fett-scenario-accent-text)',
              fontSize: '12px',
              marginLeft: '24px',
            }}
            onClick={() => setFoldCtl(false)}
          >
            {t('scene.chatWorkspace.orderTab.action.showMore')}
            <DownOutlined />
          </Button>
        ) : null}
      </LogisticsDetail>
    );
  });

  const screenshotPackages = useMemo(() => {
    return logisticsPackageList.map((logisticsPackage) => ({
      trackingNumber: logisticsPackage.trackingNumber,
      shippingCarrier: logisticsPackage.shippingCarrier,
      trackingInfo: logisticsPackage.trackingInfo.map((tracking) => ({
        updateTime: tracking.updateTime,
        description: tracking.description,
      })),
    }));
  }, [logisticsPackageList]);

  return (
    <Container {...otherProps}>
      {showCopyAll && !exportMode && conversation ? (
        <Toolbar>
          <div>
            {copyAllUI}
            <SendLogisticsInfo
              platform={conversation.channel}
              region={conversation.region}
              btnText={t('scene.chatWorkspace.orderTab.logistics.action.generateScreenshot')}
              logisticsPackages={screenshotPackages}
              onClick={onHide}
            />
          </div>
          <div>{transUI}</div>
        </Toolbar>
      ) : null}
      {!isTikTok || list.length <= 1 || fold ? (
        list
      ) : (
        <Tabs size="small">
          {list.map((content, index) => {
            return (
              <Tabs.TabPane
                key={index}
                tab={`${t('scene.chatWorkspace.orderTab.logistics.label.package')} ${index + 1}`}
              >
                {content}
              </Tabs.TabPane>
            );
          })}
        </Tabs>
      )}
    </Container>
  );
}

export default LogisticsList;

const stepsClassName = css`
  margin-top: 4px;

  .ant-steps-item-icon {
    width: 8px !important;
    height: 8px !important;
    margin-right: 6px !important;
  }

  .ant-steps-item-title {
    font-size: 14px !important;
    line-height: 18px !important;
    margin-top: 4px !important;
    margin-bottom: 2px !important;
  }

  .ant-steps-item-description {
    font-size: 14px;
    line-height: 18px;
  }

  .ant-steps-item-process
    > .ant-steps-item-container
    > .ant-steps-item-content
    > .ant-steps-item-description {
    color: var(--fett-text-tertiary);
  }
`;
